import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Container,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CalendarHeatmap from "react-calendar-heatmap";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import "react-calendar-heatmap/dist/styles.css";
import useCodingDashboardStore from "../../store/useCodingDashboardStore";

const StudentCodingDashboard = () => {
  const DAILY_GOAL = 2;
  const WEEKLY_GOAL = 10;
  const summary = useCodingDashboardStore((s) => s.summary);
  const heatmap = useCodingDashboardStore((s) => s.heatmap);
  const trend = useCodingDashboardStore((s) => s.trend);
  const recentActivity = useCodingDashboardStore((s) => s.recentActivity);
  const loading = useCodingDashboardStore((s) => s.loading);
  const error = useCodingDashboardStore((s) => s.error);
  const fetchCodingDashboard = useCodingDashboardStore(
    (s) => s.fetchCodingDashboard,
  );
  const [trendRange, setTrendRange] = useState(30);

  useEffect(() => {
    fetchCodingDashboard();
  }, [fetchCodingDashboard]);

  const cards = [
    { label: "Total Solved", value: summary?.totalSolved ?? 0 },
    { label: "Easy", value: summary?.easy ?? 0 },
    { label: "Medium", value: summary?.medium ?? 0 },
    { label: "Hard", value: summary?.hard ?? 0 },
    {
      label: "Current Streak",
      value: summary?.currentStreakDays ?? 0,
      suffix: "days",
      highlight: true,
      icon: "🔥",
      format: (v) => `${v} Day Streak`,
    },
    {
      label: "Longest Streak",
      value: summary?.longestStreakDays ?? 0,
      suffix: "days",
      highlight: false,
      format: (v) => `${v} days`,
    },
  ];
  const currentStreakDays = Number(summary?.currentStreakDays || 0);
  const streakMessage =
    currentStreakDays > 0
      ? `🔥 You're on a ${currentStreakDays} day streak!`
      : "Start solving today to build your streak";
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 364);
  const hasActivity = Array.isArray(heatmap) && heatmap.length > 0;
  const hasTrend = Array.isArray(trend) && trend.length > 0;
  const hasRecentActivity =
    Array.isArray(recentActivity) && recentActivity.length > 0;
  const visibleRecentActivity = useMemo(
    () => (Array.isArray(recentActivity) ? recentActivity.slice(0, 10) : []),
    [recentActivity],
  );
  const filteredTrend = useMemo(() => {
    if (!Array.isArray(trend)) return [];
    return trend.slice(-trendRange);
  }, [trend, trendRange]);
  const { dailySolved, weeklySolved, dailyProgress, weeklyProgress } = useMemo(() => {
    const normalizeDateKey = (value) => {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      d.setUTCHours(0, 0, 0, 0);
      return d.toISOString().slice(0, 10);
    };

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayKey = today.toISOString().slice(0, 10);
    const weekStart = new Date(today);
    weekStart.setUTCDate(weekStart.getUTCDate() - 6);
    const weekStartKey = weekStart.toISOString().slice(0, 10);

    const rows = Array.isArray(heatmap) ? heatmap : [];
    const dailyCount = rows.reduce((sum, entry) => {
      const key = normalizeDateKey(entry?.date);
      if (key !== todayKey) return sum;
      return sum + Number(entry?.count || 0);
    }, 0);

    const weekCount = rows.reduce((sum, entry) => {
      const key = normalizeDateKey(entry?.date);
      if (!key) return sum;
      if (key < weekStartKey || key > todayKey) return sum;
      return sum + Number(entry?.count || 0);
    }, 0);

    return {
      dailySolved: dailyCount,
      weeklySolved: weekCount,
      dailyProgress: Math.min(100, (dailyCount / DAILY_GOAL) * 100),
      weeklyProgress: Math.min(100, (weekCount / WEEKLY_GOAL) * 100),
    };
  }, [heatmap]);

  const classForValue = (value) => {
    if (!value || value.count === 0) return "color-empty";
    if (value.count <= 2) return "color-scale-1";
    if (value.count <= 5) return "color-scale-2";
    if (value.count <= 8) return "color-scale-3";
    return "color-scale-4";
  };

  const difficultyColor = (difficulty) => {
    if (difficulty === "Easy") return "success";
    if (difficulty === "Medium") return "warning";
    if (difficulty === "Hard") return "error";
    return "default";
  };

  const statusColor = (status) => (status === "accepted" ? "success" : "error");

  const formatStatus = (status) =>
    status === "accepted" ? "Accepted" : "Wrong";

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchCodingDashboard()}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Your Coding Summary
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: currentStreakDays > 0 ? "success.main" : "text.secondary",
              }}
            >
              {streakMessage}
            </Typography>
            <Grid container spacing={2}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={2} key={card.label}>
                  <Card>
                    <CardContent sx={{ py: 3 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {card.label}
                      </Typography>
                      {loading ? (
                        <Skeleton variant="text" width="60%" height={42} />
                      ) : (
                        <Box>
                          <Typography
                            variant={card.highlight ? "h3" : "h4"}
                            fontWeight={700}
                            sx={
                              card.highlight ? { lineHeight: 1.1 } : undefined
                            }
                          >
                            {card.icon ? `${card.icon} ` : ""}
                            {card.format ? card.format(card.value) : card.value}
                          </Typography>
                          {card.suffix ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {card.suffix}
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Activity
            </Typography>
            <Card>
              <CardContent sx={{ py: 3 }}>
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 140,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : hasActivity ? (
                  <Box
                    sx={{
                      maxWidth: 760,
                      "& .react-calendar-heatmap .color-empty": {
                        fill: "#ebedf0",
                      },
                      "& .react-calendar-heatmap .color-scale-1": {
                        fill: "#c6e48b",
                      },
                      "& .react-calendar-heatmap .color-scale-2": {
                        fill: "#7bc96f",
                      },
                      "& .react-calendar-heatmap .color-scale-3": {
                        fill: "#239a3b",
                      },
                      "& .react-calendar-heatmap .color-scale-4": {
                        fill: "#196127",
                      },
                      "& .react-calendar-heatmap rect": { width: 9, height: 9 },
                      "& .react-calendar-heatmap text": { fontSize: "9px" },
                    }}
                  >
                    <CalendarHeatmap
                      startDate={startDate}
                      endDate={endDate}
                      values={heatmap}
                      classForValue={classForValue}
                      titleForValue={(value) =>
                        value?.date
                          ? `${value.date}: ${value.count || 0} submissions`
                          : "No submissions"
                      }
                      showWeekdayLabels
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Goals Progress
            </Typography>
            <Card>
              <CardContent sx={{ py: 3 }}>
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 80,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ display: "grid", gap: 2.5 }}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Daily Goal
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dailySolved}/{DAILY_GOAL} problems
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={dailyProgress}
                        sx={{ height: 8, borderRadius: 999 }}
                      />
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Weekly Goal
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {weeklySolved}/{WEEKLY_GOAL} problems
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={weeklyProgress}
                        sx={{ height: 8, borderRadius: 999 }}
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Recent Activity
            </Typography>
            <Card>
              <CardContent sx={{ py: 1 }}>
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : hasRecentActivity ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Problem Name</TableCell>
                          <TableCell>Difficulty</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRecentActivity.map((item, index) => (
                          <TableRow key={`${item?.title || "problem"}-${index}`}>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" noWrap>
                                {item?.title || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item?.difficulty || "Unknown"}
                                color={difficultyColor(item?.difficulty)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatStatus(item?.status)}
                                color={statusColor(item?.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{item?.date || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No recent activity
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Progress Over Time
            </Typography>
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                >
                  <ToggleButtonGroup
                    size="small"
                    value={trendRange}
                    exclusive
                    onChange={(_e, next) => {
                      if (next) setTrendRange(next);
                    }}
                  >
                    <ToggleButton value={7}>7d</ToggleButton>
                    <ToggleButton value={30}>30d</ToggleButton>
                    <ToggleButton value={90}>90d</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 100,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : hasTrend ? (
                  <Box sx={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                      <ComposedChart data={filteredTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          minTickGap={18}
                        />
                        <YAxis yAxisId="left" allowDecimals={false} />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          allowDecimals={false}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                          yAxisId="right"
                          dataKey="dailyAccepted"
                          name="Daily Accepted"
                          fill="#7bc96f"
                          barSize={10}
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="totalSolved"
                          name="Total Solved"
                          stroke="#1f77b4"
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default StudentCodingDashboard;
